import React, { useCallback, useContext, useMemo } from "react";
import { IMQTTListenerContextProps } from "./MQTTListenerContextInterfaces";
import { MQTTListenerContext } from "./MQTTListenerContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { IPLCEvent } from "mqtt/entities/EventReport/EventReportInterfaces";
import useUptimeDowntimeActionsHMI from "hooks/useUptimeDowntimeActionsHMI";
import useMQTTListener from "hooks/useMQTTListener";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { getClusterIdHMI } from "utilities/storageUtilities";

export const MQTTListenerContextProvider: React.FC<IMQTTListenerContextProps> = ({ children }) => {
    const {
        store: { machineCentersCluster },
    } = useContext(ClusterCachedDataContext);
    const {
        store: { machineCenter },
    } = useContext(HMICachedDataContext);

    const isClusterAllowed = useMemo(
        () =>
            machineCenter?.production_line?.mill?.cluster_enabled ||
            machineCentersCluster[0]?.production_line?.mill?.cluster_enabled,
        [machineCentersCluster, machineCenter],
    );
    const isCluster = useMemo(() => Boolean(getClusterIdHMI()) && isClusterAllowed, [isClusterAllowed]);
    const isPLC = useMemo(
        () =>
            isCluster
                ? Boolean(
                      machineCentersCluster?.find(
                          (machineCenterCluster) =>
                              machineCenterCluster.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
                              Boolean(machineCenterCluster?.plc_address),
                      ),
                  )
                : machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
                  Boolean(machineCenter?.plc_address),
        [machineCenter?.downtime_method_capture_id, machineCenter?.plc_address, isCluster, machineCentersCluster],
    );

    const { handlePLCMQTTMessagesHMI } = useUptimeDowntimeActionsHMI();

    const onMessageCallback = useCallback(
        (event: IPLCEvent) => {
            if (isPLC) {
                handlePLCMQTTMessagesHMI(event);
            }
        },
        [isPLC, handlePLCMQTTMessagesHMI],
    );
    const { isListening } = useMQTTListener(onMessageCallback, isPLC);

    const providerValue = useMemo(
        () => ({
            isListening,
        }),
        [isListening],
    );

    return <MQTTListenerContext.Provider value={providerValue}>{children}</MQTTListenerContext.Provider>;
};
